// =============================================================================
// PREVIOUS IMPLEMENTATION — kept verbatim below for reference, intentionally
// inert. Line comments rather than a /* */ block: the original text contains
// comment terminators, which would close a block comment early.
//
// The active implementation follows after this block. Nothing above it is live
// code — it is here so the previous behaviour stays readable in place.
// =============================================================================

// import React, { FC } from "react";
// import { Helmet } from "react-helmet";
// import { Link } from "react-router-dom";
//
// export interface TenantDashboardPageProps {
//   className?: string;
// }
//
// // TODO: this index exists so the onboarding screens can be found and reviewed
// // while they are being built. In production a tenant arrives on step 1 through
// // the secure link generated per reservation, never through a list like this —
// // remove it once that entry point exists.
// const STEPS: { step: number; title: string; path?: string; note?: string }[] = [
//   { step: 1, title: "Welcome", path: "/tenant/welcome" },
//   { step: 2, title: "Verify Identity", path: "/tenant/verify-identity" },
//   { step: 3, title: "Review & Sign Contract", path: "/tenant/sign-contract" },
//   {
//     step: 4,
//     title: "Ready for your Arrival",
//     path: "/tenant/ready-for-arrival",
//     note: "Flow A ends here",
//   },
//   { step: 5, title: "Property Review & Inventory", path: "/tenant/inventory" },
//   { step: 6, title: "Protect your Stay", path: "/tenant/insurance" },
//   { step: 7, title: "Welcome Home", path: "/tenant/welcome-home" },
// ];
//
// const TenantDashboardPage: FC<TenantDashboardPageProps> = ({
//   className = "",
// }) => {
//   return (
//     <div
//       className={`nc-TenantDashboardPage ${className}`}
//       data-nc-id="TenantDashboardPage"
//     >
//       <Helmet>
//         <title>Tenant Platform | Europea</title>
//       </Helmet>
//
//       <div className="container max-w-2xl py-16 lg:py-24">
//         <h1 className="text-3xl font-semibold md:text-4xl">
//           Digital Onboarding
//         </h1>
//         <p className="mt-3 text-neutral-500 dark:text-neutral-400">
//           Screen index for the tenant platform. Flow A is steps 1&ndash;4, Flow
//           B is steps 1&ndash;7.
//         </p>
//
//         <ul className="mt-8 flex flex-col gap-2">
//           {STEPS.map(({ step, title, path, note }) => {
//             const content = (
//               <>
//                 <span
//                   className={`flex h-8 w-8 flex-none items-center justify-center rounded-full text-sm font-semibold ${
//                     path
//                       ? "bg-primary-6000 text-white"
//                       : "bg-neutral-100 text-neutral-400 dark:bg-neutral-800"
//                   }`}
//                 >
//                   {step}
//                 </span>
//                 <span className="flex-1">
//                   <span className="font-medium">{title}</span>
//                   {note && (
//                     <span className="ml-2 text-xs text-neutral-400">
//                       {note}
//                     </span>
//                   )}
//                 </span>
//                 <span className="text-xs text-neutral-400">
//                   {path || "not built yet"}
//                 </span>
//               </>
//             );
//
//             return (
//               <li key={step}>
//                 {path ? (
//                   <Link
//                     to={path}
//                     className="flex items-center gap-3 rounded-xl border border-neutral-200 p-3 transition-colors hover:border-primary-6000 dark:border-neutral-700"
//                   >
//                     {content}
//                   </Link>
//                 ) : (
//                   <div className="flex items-center gap-3 rounded-xl border border-dashed border-neutral-200 p-3 opacity-60 dark:border-neutral-700">
//                     {content}
//                   </div>
//                 )}
//               </li>
//             );
//           })}
//         </ul>
//       </div>
//     </div>
//   );
// };
//
// export default TenantDashboardPage;

// ==== ACTIVE IMPLEMENTATION ==================================================


import React, { FC, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import OnboardingWorkspace from "./desktop/OnboardingWorkspace";
import SignatureCapture from "./desktop/SignatureCapture";
import { STEP_PANELS } from "./desktop/panels";
import {
  INITIAL_ONBOARDING_STATE,
  OnboardingState,
  SignatureTarget,
} from "./desktop/panels/types";
import {
  Flow,
  StepId,
  stepsForFlow,
  stepsForToggles,
} from "./desktop/onboardingSteps";
import {
  hasAnnex,
  localeForLanguage,
  submitContractSignature,
  submitInventory,
  toReservation,
  useOnboarding,
} from "./desktop/onboardingConfig";
import { ReservationProvider } from "./desktop/reservationContext";
import { OnboardingProvider } from "./desktop/onboardingContext";
import { RESERVATION } from "./desktop/reservation";
import { setLocale, useLocale } from "./i18n/locale";

export interface TenantDashboardPageProps {
  className?: string;
  /**
   * The journey to draw when there is no reservation behind the screen — a
   * direct visit or a design review. A real tenant arrives with ?id=… and the
   * journey comes from the DMS instead.
   */
  flow?: Flow;
}

/**
 * /tenant — the desktop onboarding workspace.
 *
 * The step lives in state rather than in the URL: the rail, the footer and the
 * centre column all move together, and the phone journey already owns the
 * routed one-step-per-URL form (/tenant/welcome and friends). Give this its own
 * routes only when a tenant needs to link into a single step.
 *
 * Everything the tenant has done is held here rather than in the panels. The
 * rail describes that progress, and a panel that owned it would throw it away
 * the moment the tenant looked at another step.
 */
const TenantDashboardPage: FC<TenantDashboardPageProps> = ({
  className = "",
  flow = "B",
}) => {
  const { t } = useLocale();

  // The tenant's link carries the id of the document set they were sent, which
  // is what the DMS keyed their journey against.
  const [searchParams] = useSearchParams();
  const generatedPdfId = searchParams.get("id");
  const { onboarding, loading, markStepComplete } = useOnboarding(generatedPdfId);

  // The journey the back office configured, or the static one when no
  // reservation is behind the screen.
  const steps = onboarding ? stepsForToggles(onboarding) : stepsForFlow(flow);
  const reservation = onboarding ? toReservation(onboarding.reservation) : RESERVATION;

  /**
   * Whether every step this reservation includes is behind the tenant.
   *
   * Read off the steps rather than status: markStepComplete updates them
   * optimistically but leaves status as the server last sent it, so a tenant
   * who finishes the final step in this session would otherwise keep a live
   * rail until they reloaded. Welcome is skipped — it is never stored.
   */
  const journeyComplete =
    !!onboarding &&
    steps.every(
      (step) =>
        step.id === "welcome" ||
        onboarding.steps[step.id]?.state === "completed"
    );

  // Opens on welcome, which introduces the stay before anything is asked of
  // the tenant; a real tenant is moved to wherever they left off as soon as
  // the journey loads, so only someone starting fresh stays on it.
  const [activeId, setActiveId] = useState<StepId>("welcome");
  const [state, setState] = useState<OnboardingState>(INITIAL_ONBOARDING_STATE);

  /**
   * Whether the tenant has been placed on their saved step yet.
   *
   * The placement must happen exactly once, when the journey first arrives.
   * Completing a step changes the record, so re-running it would drag the
   * tenant back to the step they had just finished the moment they moved on.
   */
  const positioned = useRef(false);

  // The DMS decides the tenant's language, whether an annex was sent — the
  // annex is what makes the signature screen say it covers two documents — and
  // how far they had already got.
  useEffect(() => {
    if (!onboarding) return;
    // Hydrate the local editing state from the record exactly once, when the
    // journey first arrives. After that `state` is authoritative — re-running
    // this overwrites in-session edits with the record. markStepComplete
    // optimistically replaces the onboarding object (to tick the rail) without
    // carrying the tenant's latest inventory comments/photos, so a re-sync here
    // wiped the comment and photos the moment the inventory step was signed.
    if (positioned.current) return;
    positioned.current = true;

    setLocale(localeForLanguage(onboarding.language));
    setState((current) => ({
      ...current,
      scenario: hasAnnex(onboarding) ? "contract+attestation" : "contract-only",
      documentsSigned: onboarding.contract_signed,
      // Steps 5 and 6 happen days apart, so a tenant coming back must see the
      // inventory they already filed rather than an empty form.
      inventoryComments: onboarding.inventory.comments,
      inventoryPhotoUrls: onboarding.inventory.photo_urls,
      inventorySigned: !!onboarding.inventory.signed_at,
      insuranceSent: !!onboarding.insurance.file_url,
      // Same reason: step 2 has to show the document already filed rather than
      // an empty dropzone when the tenant comes back to it.
      identityFileName: onboarding.identity.file_name,
    }));

    // Steps 5 and 6 happen days after check-in, so the tenant returns to a
    // half-finished journey and must land on the step they stopped at.
    // current_step is "identity" for a journey nobody has started, which
    // would skip the welcome. A tenant with nothing completed is opening
    // their link for the first time and gets the introduction; everyone else
    // returns to where they stopped.
    const untouched = Object.values(onboarding.steps).every(
      (step) => step?.state !== "completed"
    );
    // A tenant returning to a finished journey is coming back to read it,
    // not to continue it, so they land on the closing step rather than being
    // dropped back at the start.
    if (journeyComplete) {
      setActiveId(steps[steps.length - 1].id);
      return;
    }
    setActiveId(untouched ? "welcome" : onboarding.current_step);
  }, [onboarding]);

  /** Non-null while the signature screen has taken over the centre column. */
  const [signing, setSigning] = useState<SignatureTarget | null>(null);

  /**
   * The closing screen has nothing to do on it, so reaching it is what
   * completes it.
   *
   * Keyed off the flow's own terminal step rather than a hard-coded "home":
   * Flow B ends on "home", but Flow A ends on "arrival" — which has no Continue
   * button (nothing comes after it) and so was never recorded, leaving the
   * journey stuck at "in_progress" for ever. Whichever step is last, landing on
   * it is what finishes it — which is also what stamps the retention date on the
   * identity document.
   */
  const lastStepId = steps[steps.length - 1]?.id;
  useEffect(() => {
    if (!onboarding) return;
    if (!lastStepId || activeId !== lastStepId) return;
    if (onboarding.steps[lastStepId]?.state === "completed") return;
    markStepComplete(lastStepId);
  }, [onboarding, activeId, lastStepId, markStepComplete]);

  const index = Math.max(
    steps.findIndex((step) => step.id === activeId),
    0
  );

  // A step the DMS turned off is no longer in the rail, so fall back to the
  // first one rather than rendering a panel the tenant cannot navigate back to.
  const currentId = steps[index]?.id ?? activeId;

  const update = (patch: Partial<OnboardingState>) =>
    setState((current) => ({ ...current, ...patch }));

  const goTo = (id: StepId) => {
    // The single gate for every route into a step — the rail, a panel's Back
    // button, a panel jumping the tenant somewhere. A closed journey ignores
    // all of them rather than each caller having to remember.
    if (journeyComplete) return;
    setActiveId(id);
    setSigning(null);
    // The rail is taller than the centre column on the short steps, so a jump
    // from step 7 to step 1 would otherwise land mid-page.
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /**
   * Move on, recording the step just finished.
   *
   * Only the Continue button reports progress — jumping around the rail is the
   * tenant looking back at something, not completing it. The write is not
   * awaited: someone who has done the work should not be held on the screen
   * while it saves, and the step is recorded again if they pass through twice.
   */
  const completeAndGoTo = (finished: StepId, next: StepId) => {
    // Welcome is not a stored step — opening the link is what completes it —
    // so there is nothing for the DMS to record when the tenant moves on.
    if (finished !== "welcome") markStepComplete(finished);
    goTo(next);
  };

  const startSigning = (target: SignatureTarget) => {
    setSigning(target);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /** True while the contract signature is being sent to the DMS. */
  const [submittingSignature, setSubmittingSignature] = useState(false);
  const [signatureError, setSignatureError] = useState(false);

  const confirmSignature = async (signature: string) => {
    // One signature covers every document in the set it was opened for.
    if (signing === "documents") {
      // Nothing is marked signed until the DMS has it. Showing success on a
      // failed save would leave the tenant believing they had signed a lease
      // that nobody holds.
      if (generatedPdfId) {
        setSubmittingSignature(true);
        setSignatureError(false);
        const saved = await submitContractSignature(generatedPdfId, signature);
        setSubmittingSignature(false);
        if (!saved) {
          setSignatureError(true);
          return;
        }
        // Deliberately NOT marked complete here. Signing files the signature;
        // pressing Continue is what finishes the step, as it does for every
        // other one. Completing it at signature time made "completed" mean
        // "signed", which left nothing able to tell a tenant still on the step
        // (who may redraw a poor signature) from one who has moved past it (who
        // must not) — and it advanced current_step while they were still here.
      }
      update({ documentsSigned: true, documentSignature: signature });
    } else if (signing === "inventory") {
      // The signature is what commits the inventory, so the comments and photos
      // go up with it rather than on their own. Nothing is marked signed until
      // the DMS has all three.
      if (generatedPdfId) {
        setSubmittingSignature(true);
        setSignatureError(false);
        const saved = await submitInventory(
          generatedPdfId,
          state.inventoryComments,
          state.tenantPhotos,
          signature
        );
        setSubmittingSignature(false);
        if (!saved) {
          setSignatureError(true);
          return;
        }
        // The files have been uploaded; keep the URLs and drop the bodies.
        update({ tenantPhotos: [], inventoryPhotoUrls: saved.photo_urls });
        // Completed on Continue, not here — see the contract branch above.
        // complete_step still verifies inventory.signed_at exists before it
        // records the step (STEP_EVIDENCE), so nothing is lost by waiting.
      }
      update({ inventorySigned: true, inventorySignature: signature });
    }
    setSigning(null);
  };

  const Panel = STEP_PANELS[currentId];
  const hasAttestation = state.scenario === "contract+attestation";
  const c = t.desktop.signaturePanel;

  /** What the pending signature will be applied to, named on that screen. */
  const documentLabel =
    signing === "inventory"
      ? c.labelInventory
      : hasAttestation
      ? c.labelBoth
      : c.labelContract;

  // Hold the workspace back until the journey is known. Rendering the default
  // seven steps first would show a tenant an inventory step that is then pulled
  // out from under them a moment later.
  if (loading) {
    return (
      <div
        className={`nc-TenantDashboardPage min-h-screen bg-cream-page ${className}`}
        data-nc-id="TenantDashboardPage"
      />
    );
  }

  return (
    <ReservationProvider reservation={reservation}>
    <OnboardingProvider onboarding={onboarding}>
    <div
      className={`nc-TenantDashboardPage ${className}`}
      data-nc-id="TenantDashboardPage"
    >
      <Helmet>
        <title>{t.desktop.documentTitle}</title>
      </Helmet>

      <OnboardingWorkspace
        steps={steps}
        activeId={currentId}
        state={state}
        onSelect={goTo}
        locked={journeyComplete}
      >
        {signing ? (
          // Signing takes over the column rather than opening a dialog — it is
          // the whole task at that moment.
          <SignatureCapture
            documentLabel={documentLabel}
            plural={signing === "documents" && hasAttestation}
            backLabel={
              signing === "inventory" ? c.backToInventory : c.backToDocuments
            }
            onBack={() => setSigning(null)}
            onConfirm={confirmSignature}
            submitting={submittingSignature}
            failed={signatureError}
          />
        ) : (
          <Panel
            steps={steps}
            state={state}
            update={update}
            goTo={goTo}
            startSigning={startSigning}
            onBack={index > 0 ? () => goTo(steps[index - 1].id) : undefined}
            onContinue={
              index < steps.length - 1
                ? () => completeAndGoTo(currentId, steps[index + 1].id)
                // Absent on the last step, which is how the panels know not to
                // draw a Continue button. Closing the journey is that step's
                // own action, wired with the rest of step 7.
                : undefined
            }
          />
        )}
      </OnboardingWorkspace>
    </div>
    </OnboardingProvider>
    </ReservationProvider>
  );
};

export default TenantDashboardPage;
